import React from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTheme } from './ThemeContext';

/**
 * UpgradePrompt — shown when a student clicks something their plan doesn't include.
 * The wording is generic, so any locked item (a video, or a course card on the
 * Library page) can use it.
 *
 * Props: open (bool), onClose (fn).
 *
 * Rendered through a portal onto document.body so no parent's layout, overflow
 * or animation can clip it. Portals keep React context, so the router and theme
 * still reach it.
 */
export default function UpgradePrompt({ open, onClose }) {
  const navigate = useNavigate();
  const { colors, isLight } = useTheme();

  if (!open) return null;

  // Keys stay inside the prompt. Without this, the video pages' window-level
  // shortcuts would see Space and toggle playback (and preventDefault it, so the
  // focused button would never be pressed). Focus starts inside the prompt
  // (autoFocus on Upgrade), so key events always come from here.
  const onKeyDown = (e) => {
    e.stopPropagation();
    if (e.key === 'Escape') onClose();
  };

  return createPortal(
    <div
      onClick={onClose}
      onKeyDown={onKeyDown}
      style={{
        position: 'fixed', inset: 0, zIndex: 1100,
        background: isLight ? 'rgba(0,0,0,0.35)' : 'rgba(0,0,0,0.65)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px',
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="upgrade-prompt-text"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: '420px', boxSizing: 'border-box',
          padding: '28px 28px 24px', borderRadius: '18px', textAlign: 'center',
          background: colors.cardBg || colors.bg,
          border: `1px solid ${isLight ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.10)'}`,
          boxShadow: '0 24px 64px rgba(0,0,0,0.35)',
          fontFamily: '"DM Sans", sans-serif',
        }}
      >
        <p id="upgrade-prompt-text" style={{ margin: '0 0 22px', fontSize: '16px', lineHeight: 1.5, color: colors.text }}>
          This isn’t included in your current plan. Upgrade to access it.
        </p>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              minHeight: '44px', padding: '0 20px', borderRadius: '22px', cursor: 'pointer',
              background: 'transparent', color: colors.text, fontSize: '15px', fontFamily: 'inherit',
              border: `1px solid ${isLight ? 'rgba(0,0,0,0.18)' : 'rgba(255,255,255,0.20)'}`,
            }}
          >
            Not now
          </button>
          <button
            type="button"
            autoFocus
            onClick={() => { onClose(); navigate('/subscription'); }}
            style={{
              minHeight: '44px', padding: '0 24px', borderRadius: '22px', cursor: 'pointer', border: 0,
              // Light theme: the free accent is too pale for readable white text, so
              // the button uses the text colour instead.
              background: isLight ? colors.text : colors.accent, color: isLight ? '#FFFFFF' : '#1A1A1A',
              fontSize: '15px', fontWeight: 600, fontFamily: 'inherit',
            }}
          >
            Upgrade
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
